"use client";

import * as React from "react";
import Link from "next/link";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Validates if a URL is safe and well-formed
 * @param url - The URL to validate
 * @returns true if the URL is valid, false otherwise
 */
function isValidUrl(url: string): boolean {
  // Allow internal paths (starting with /)
  if (url.startsWith("/")) {
    return true;
  }

  // Allow anchor links
  if (url.startsWith("#")) {
    return true;
  }

  // Validate external URLs
  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols
    return urlObj.protocol === "http:" || urlObj.protocol === "https:";
  } catch {
    return false;
  }
}

const buttonLinkVariants = cva(
  "inline-flex items-center justify-center gap-2 font-bold transition-all",
  {
    variants: {
      variant: {
        primary: "bg-primary text-white shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98]",
        "primary-secondary": "bg-secondary text-foreground shadow-xl shadow-secondary/20 hover:scale-[1.02] active:scale-[0.98]",
        secondary: "bg-white text-foreground border border-zinc-100 hover:scale-[1.02] active:scale-[0.98]",
        "secondary-dark": "bg-foreground text-white shadow-xl shadow-foreground/10 hover:scale-[1.02] active:scale-[0.98]",
        text: "bg-transparent text-white hover:text-secondary transition-colors uppercase tracking-widest",
      },
      size: {
        sm: "px-8 py-3.5 text-sm rounded-xl",
        md: "px-10 py-4 text-lg rounded-2xl",
        lg: "px-12 py-5 text-xl rounded-2xl",
        text: "text-xs",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonLinkProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof buttonLinkVariants> {
  href?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const ButtonLink = React.forwardRef<HTMLElement, ButtonLinkProps>(
  ({ className, variant, size, href, icon, children, ...props }, ref) => {
    const classes = cn(buttonLinkVariants({ variant, size, className }));

    // If href is provided and not empty, validate and render as Link
    if (href && href.trim()) {
      const trimmedHref = href.trim();

      // Validate URL and warn in development if invalid
      if (!isValidUrl(trimmedHref)) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `ButtonLink: Invalid URL detected: "${trimmedHref}". ` +
            `URLs must be internal paths (starting with /), anchor links (starting with #), ` +
            `or valid external URLs (http:// or https://). Rendering as disabled button.`
          );
        }

        // Render as disabled span if URL is invalid
        return (
          <span
            className={cn(classes, "pointer-events-none opacity-50")}
            aria-disabled="true"
            aria-label={typeof children === 'string' ? children : 'Disabled button'}
            {...props}
            ref={ref as React.Ref<HTMLSpanElement>}
          >
            {children}
            {icon}
          </span>
        );
      }

      return (
        <Link
          href={trimmedHref}
          className={classes}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          ref={ref as React.Ref<HTMLAnchorElement>}
        >
          {children}
          {icon}
        </Link>
      );
    }

    // If no href, render as span with pointer-events-none
    return (
      <span
        className={cn(classes, "pointer-events-none")}
        aria-disabled="true"
        aria-label={typeof children === 'string' ? children : 'Disabled button'}
        {...props}
        ref={ref as React.Ref<HTMLSpanElement>}
      >
        {children}
        {icon}
      </span>
    );
  }
);

ButtonLink.displayName = "ButtonLink";

export { ButtonLink, buttonLinkVariants };
